import {
  ACCOUNT_QUERY_STOCK_TRANSFER,
  ACCOUNT_QUERY_STOCK_TRANSFER_HISTORY,
  ACCOUNT_QUERY_STOCK_TRANSFER_HISTORY_LOAD_MORE,
  ACCOUNT_SUBMIT_STOCK_TRANSFER,
} from './actions';
import {
  BrokerFunction,
  IAction,
  IError,
  INotification,
} from 'interfaces/common';
import {
  COMMON_SHOW_NOTIFICATION,
  QUERY_BROKER_PHONE_CODE_SUCCESS,
  ACCOUNT_OTP_VERIFY_SUCCESS,
  GET_SIGN_TOKENCA,
} from 'redux/actions';
import { IState } from 'redux/global-reducers';
import {
  IStockTransferHistoryParams,
  IStockTransferHistoryResponse,
  IStockTransferParams,
  IStockTransferResponse,
  IStockTransferSubmitParams,
  IStockTransferSubmitResponse,
  ISignTokenCAResponse,
  ISaveSignTokenCAResponse,
} from 'interfaces/api';
import { ToastType } from 'react-toastify';
import { call, put, select, takeEvery, takeLeading } from 'redux-saga/effects';
import { query } from 'utils/socketApi';
import config from 'config';
import { signTokenCA, registerTokenCA } from '../../../../utils/signTokenCA';
import { addHours } from 'date-fns';

const queryStockTransfer = (params: IStockTransferParams) => {
  return query(config.apis.queryStockTransfer, params);
};

const queryStockTransferHistory = (params: IStockTransferHistoryParams) => {
  return query(config.apis.queryStockTransferHistory, params);
};

const submitStockTransfer = (params: IStockTransferSubmitParams) => {
  if (params.pin && params.clientID) {
    return query(config.apis.brokerSubmitStockTransfer, params);
  } else {
    return query(config.apis.submitStockTransfer, params);
  }
};

function* doQueryStockTransfer(request: IAction<IStockTransferParams>) {
  try {
    const response = yield call(queryStockTransfer, request.payload);

    if (request.response) {
      yield put<IAction<IStockTransferResponse[]>>({
        type: request.response.success,
        payload: response.data,
      });
    }

    const store: IState = yield select((state: IState) => ({
      selectedAccount: state.selectedAccount,
    }));

    if (store.selectedAccount?.isBrokerCall) {
      yield put({
        type: QUERY_BROKER_PHONE_CODE_SUCCESS,
        payload: {
          pin: request.payload.pin,
          account: request.payload.accountNo,
          isAuthorized: true,
          function: BrokerFunction.STOCK_TRANSFER,
        },
      });
    }
  } catch (error) {
    console.error('Query stock transfer', error);

    if (request.response) {
      yield put<IAction<string>>({
        type: request.response.failed,
        payload: error.code || error.message,
      });
    }

    yield put<IAction<INotification>>({
      type: COMMON_SHOW_NOTIFICATION,
      payload: {
        type: ToastType.ERROR,
        title: 'Stock Transfer',
        content: error.code || error.message,
        contentParams: error.messageParams,
        time: new Date(),
      },
    });
  }
}

function* doQueryStockTransferHistory(
  request: IAction<IStockTransferHistoryParams>
) {
  try {
    const response = yield call(queryStockTransferHistory, request.payload);

    if (request.response) {
      yield put<IAction<IStockTransferHistoryResponse[]>>({
        type: request.response.success,
        payload: response.data,
      });
    }
  } catch (error) {
    console.error('Query stock transfer history', error);

    if (request.response) {
      yield put<IAction<string>>({
        type: request.response.failed,
        payload: error.code || error.message,
      });
    }

    yield put<IAction<INotification>>({
      type: COMMON_SHOW_NOTIFICATION,
      payload: {
        type: ToastType.ERROR,
        title: 'Stock Transfer History',
        content: error.code || error.message,
        contentParams: error.messageParams,
        time: new Date(),
      },
    });
  }
}

function* doSubmitStockTransfer(
  requests: IAction<IStockTransferSubmitParams[]>
) {
  const store: IState = yield select((state: IState) => ({
    signTokenCA: state.signTokenCA,
    otp: state.otp,
  }));

  const mutableResponseData: Array<IStockTransferSubmitResponse | IError> = [];

  if (store.signTokenCA?.isTokenCA) {
    const data = {
      action: 'verifyTokenStockTransfer',
      serialToken: store.signTokenCA?.serial,
      dataSign: {
        data: requests.payload
      },
      timeSign: new Date(),
    };

    if (store.signTokenCA?.serial) {
      const responseSign: ISignTokenCAResponse = yield call(
        signTokenCA,
        store.signTokenCA?.serial,
        data
      );
      if (responseSign && responseSign.code == 1) {
        let dataSubmit = {
          dataSignTokenCA: responseSign.data,
          ...data,
        };

        const responseSaveSignTokenCA: ISaveSignTokenCAResponse = yield call(
          registerTokenCA,
          {
            action: 'verifyTokenStockTransfer',
            serialToken: store.signTokenCA.serial ?? '',
            dataSign: JSON.stringify(dataSubmit),
            timeSign: addHours(new Date(), 7).toISOString() ?? '',
          }
        );

        if (responseSaveSignTokenCA && responseSaveSignTokenCA.status !== 201) {
          yield put<IAction<INotification>>({
            type: COMMON_SHOW_NOTIFICATION,
            payload: {
              type: ToastType.ERROR,
              title: 'Stock Transfer',
              content: 'STOCK_TRANSFER_FAILED',
              time: new Date(),
            },
          });
        } else {
          for (const request of requests.payload) {
            try {
              const response = yield call(submitStockTransfer, request);

              yield put<IAction<INotification>>({
                type: COMMON_SHOW_NOTIFICATION,
                payload: {
                  type: ToastType.SUCCESS,
                  title: 'Stock Transfer',
                  content: 'STOCK_TRANSFER_SYMBOL_SUCCESSFULLY',
                  contentParams: {
                    symbol: request.stockSymbol,
                  },
                  time: new Date(),
                },
              });

              mutableResponseData.push(response.data);
            } catch (error) {
              console.error('Submit stock transfer', error);

              mutableResponseData.push(error);

              yield put<IAction<INotification>>({
                type: COMMON_SHOW_NOTIFICATION,
                payload: {
                  type: ToastType.ERROR,
                  title: 'Stock Transfer',
                  content: error.code || error.message,
                  contentParams: error.messageParams,
                  time: new Date(),
                },
              });
            }
          }
          if (requests.response) {
            yield put<IAction<Array<IStockTransferSubmitResponse | IError>>>({
              type: requests.response.success,
              payload: mutableResponseData,
            });
          }
        }
      }
    }
  } else {
    for (const request of requests.payload) {
      try {
        const response = yield call(submitStockTransfer, request);

        yield put<IAction<INotification>>({
          type: COMMON_SHOW_NOTIFICATION,
          payload: {
            type: ToastType.SUCCESS,
            title: 'Stock Transfer',
            content: 'STOCK_TRANSFER_SYMBOL_SUCCESSFULLY',
            contentParams: {
              symbol: request.stockSymbol,
            },
            time: new Date(),
          },
        });

        mutableResponseData.push(response.data);
      } catch (error) {
        console.error('Submit stock transfer', error);

        mutableResponseData.push(error);

        yield put<IAction<INotification>>({
          type: COMMON_SHOW_NOTIFICATION,
          payload: {
            type: ToastType.ERROR,
            title: 'Stock Transfer',
            content: error.code || error.message,
            contentParams: error.messageParams,
            time: new Date(),
          },
        });
      }
    }
    if (requests.response) {
      yield put<IAction<Array<IStockTransferSubmitResponse | IError>>>({
        type: requests.response.success,
        payload: mutableResponseData,
      });
    }
  }

  if (store.otp?.otpToken?.data?.expiryTime == 0) {
    yield put({
      type: ACCOUNT_OTP_VERIFY_SUCCESS,
      payload: {
        token: null,
      },
    });
  }

  yield put({
    type: GET_SIGN_TOKENCA,
    payload: {
      serial: '',
      isTokenCA: false,
    },
  });
}

export function* watchQueryStockTransfer() {
  yield takeLeading(ACCOUNT_QUERY_STOCK_TRANSFER, doQueryStockTransfer);
  yield takeLeading(
    ACCOUNT_QUERY_STOCK_TRANSFER_HISTORY,
    doQueryStockTransferHistory
  );
  yield takeLeading(
    ACCOUNT_QUERY_STOCK_TRANSFER_HISTORY_LOAD_MORE,
    doQueryStockTransferHistory
  );
  yield takeEvery(ACCOUNT_SUBMIT_STOCK_TRANSFER, doSubmitStockTransfer);
}



// WEBPACK FOOTER //
// ./src/components/areas/Account/StockTransfer/sagas.ts